<template>
  <default-layout>
    <template>
      <v-container>
        <v-row>
          <!-- USER INFORMATION -->
          <v-col cols="12" md="4">
            <v-card>
              <v-card-title class="pt-6"> User Info </v-card-title>
              <v-card-text v-if="user">
                <v-simple-table>
                  <tbody>
                    <tr>
                      <th>First Name</th>
                      <td>{{ user.firstName }}</td>
                    </tr>
                    <tr>
                      <th>Last Name</th>
                      <td>{{ user.lastName }}</td>
                    </tr>
                    <tr>
                      <th>Email</th>
                      <td>{{ user.email }}</td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>{{ user.status ? "enabled" : "disabled" }}</td>
                    </tr>
                    <tr>
                      <th>Last Login</th>
                      <td>
                        <span v-if="user.lastLoginAt">{{
                          user.lastLoginAt | dateTime
                        }}</span>
                      </td>
                    </tr>
                    <tr>
                      <th>Created At</th>
                      <td>
                        <span v-if="user.createdAt">{{
                          user.createdAt | dateTime
                        }}</span>
                      </td>
                    </tr>
                    <tr>
                      <th>Updated At</th>
                      <td>
                        <span v-if="user.updatedAt">{{
                          user.updatedAt | dateTime
                        }}</span>
                      </td>
                    </tr>
                    <tr>
                      <th>Provider</th>
                      <td>
                        <router-link
                          :to="{
                            name: 'CompanyShow',
                            params: { companyId: user.companyId }
                          }"
                          >View Provider</router-link
                        >
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
              <v-card-actions v-if="user">
                <v-spacer></v-spacer>

                <v-btn
                  color="primary"
                  :to="{ name: 'UserEdit', params: { userId: user.id } }"
                  ><v-icon>mdi-account-edit</v-icon> Edit</v-btn
                >

                <!-- DELETE USER -->
                <v-dialog v-model="deleteUserDialog" width="500">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn class="ml-2" color="error" v-bind="attrs" v-on="on">
                      <v-icon>mdi-account-remove</v-icon> Delete
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title class="primary white--text">
                      Delete User?
                    </v-card-title>

                    <v-card-text class="pt-4">
                      <p class="mb-0">
                        Are you sure you want to delete this user? This action
                        will remove all information about this user from
                        Rehab.com. This action CANNOT be undone, so use your
                        superpowers wisely.
                      </p>
                      <p>
                        Type "<strong>confirm</strong>" below and then click
                        "YES" to delete.
                      </p>
                      <v-text-field
                        label="Confirm"
                        v-model="confirm"
                      ></v-text-field>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        color="error"
                        raised
                        @click="deleteConnectAccountDialog = false"
                      >
                        No
                      </v-btn>
                      <v-btn
                        color="primary"
                        raised
                        :loading="deleteUserLoading"
                        @click="deleteUser()"
                        :disabled="confirm !== 'confirm'"
                      >
                        Yes
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-card-actions>
            </v-card>
          </v-col>

          <!-- CONNECT LOGIN ACTIVITY -->
          <v-col cols="12" md="3">
            <v-card>
              <v-card-title class="pt-6"> Login History </v-card-title>
              <v-card-text>
                <v-simple-table v-if="user">
                  <tbody>
                    <tr
                      v-for="loginLog in viewableLoginLogs"
                      :key="loginLog.id"
                    >
                      <td>{{ loginLog.createdAt | dateTime }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <a
                  class="pl-4"
                  @click.stop="loginLogLimit += 5"
                  v-if="user && loginLogLimit < user.loginLogs.length"
                  >show more</a
                >
              </v-card-text>
            </v-card>
          </v-col>

          <!-- USER STATUS LOGS -->
          <v-col cols="12" md="3">
            <v-card>
              <v-card-title class="pt-6"> Status Logs </v-card-title>
              <v-card-text>
                <v-simple-table v-if="user">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Action</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="statusLog in user.statusLogs"
                      :key="statusLog.id"
                    >
                      <td>
                        {{ statusLog.name | snakeToSentence }}
                      </td>
                      <td>{{ statusLog.action }}</td>
                      <td>{{ statusLog.createdAt | dateTime }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </default-layout>
</template>

<script>
import { getUser, deleteUser } from "../../api/user";

export default {
  name: "UserShow",

  data() {
    return {
      user: null,
      loginLogLimit: 6,
      statusLogs: [],
      deleteUserDialog: false,
      confirm: "",
      deleteUserLoading: false
    };
  },

  computed: {
    viewableLoginLogs() {
      if (!this.user) {
        return [];
      } else if (this.user.loginLogs.length < this.loginLogLimit) {
        return this.user.loginLogs;
      }
      return this.user.loginLogs.slice(0, this.loginLogLimit);
    }
  },

  mounted() {
    this.getUser();
  },

  methods: {
    async getUser() {
      const userId = parseInt(this.$route.params.userId);
      if (userId) {
        this.loading = true;
        const result = await getUser(userId);
        this.user = result;
        this.loading = false;
      }
    },
    async deleteUser() {
      if (this.user && this.user.id) {
        this.deleteUserLoading = true;

        await deleteUser(this.user.id);

        this.deleteUserLoading = false;
        this.deleteUserDialog = false;
        this.$router.push({ name: "UserList" });
      }
    }
  }
};
</script>
